// Phase 2.12 Tier 3 Step 26 — dashboard tabs primitive.
//
// Tabs are a flat layout primitive — `tabs:` array `[{label, widget_keys: []}, ...]`
// on the layout JSON. When `tabs` is absent, the dashboard renders
// every widget in a single grid (existing behavior). When tabs are
// present, the controller hides every widget whose `widget_type` is
// not in the active tab's `widget_keys` set, and adds an "All" tab
// at index 0 that shows the full grid.
//
// Tab state persists in the URL (`?tab=:label`) so deep-linking works.
// History uses `replaceState` so back/forward isn't polluted.

import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

export default class extends Controller {
  static values = {
    tabs: Array,        // [{label, widget_keys: []}, ...]
    activeTab: String   // initial label from server (URL ?tab=)
  }

  static targets = ["tab", "grid"]

  connect() {
    if (!this.hasTabsValue || this.tabsValue.length === 0) return

    const initial = this.activeTabValue || this._tabFromUrl() || "All"
    this._applyTab(initial)
  }

  switch(event) {
    event.preventDefault()
    const label = event.currentTarget.dataset.tabLabel
    if (!label) return

    this._applyTab(label)
    this._writeUrl(label)
    this._track(label)
  }

  _applyTab(label) {
    this.tabTargets.forEach((el) => {
      const isActive = el.dataset.tabLabel === label
      el.classList.toggle("border-b-2", isActive)
      el.classList.toggle("border-brand", isActive)
      el.classList.toggle("text-brand", isActive)
      el.classList.toggle("text-gray-500", !isActive)
    })

    const allKeys = label === "All"
      ? null
      : new Set((this.tabsValue.find((t) => t.label === label)?.widget_keys) || [])

    if (!this.hasGridTarget) return

    this.gridTarget.querySelectorAll("[data-widget-type]").forEach((card) => {
      const wt = card.dataset.widgetType
      const visible = !allKeys || allKeys.has(wt)
      card.classList.toggle("hidden", !visible)
    })
  }

  _tabFromUrl() {
    try {
      const u = new URL(window.location.href)
      return u.searchParams.get("tab")
    } catch {
      return null
    }
  }

  _writeUrl(label) {
    try {
      const u = new URL(window.location.href)
      if (label === "All") {
        u.searchParams.delete("tab")
      } else {
        u.searchParams.set("tab", label)
      }
      window.history.replaceState({}, "", u.toString())
    } catch {
      // ignore
    }
  }

  _track(label) {
    try {
      mixpanelTrack("Dashboard Tab Switched", { tab_label: label })
    } catch (err) {
      // mixpanel not initialized (logged-out / masquerade) — ignore
    }
  }
}
